import { useState, useEffect } from 'react';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';

interface GameAuthorizationPermissions {
  canEditBasicInfo: boolean;
  canEditTeams: boolean;
  canEditSettings: boolean;
  canEditApprovalStatus: boolean;
  canViewGame: boolean;
  role: string;
  isGameCreator: boolean;
  isVenueManager: boolean;
  isTeamManager: boolean;
  isAdmin: boolean;
}

interface UseGameAuthorizationProps {
  gameData: any;
  gameId: string;
}

export const useGameAuthorization = ({ gameData, gameId }: UseGameAuthorizationProps) => {
  const { userId, permissions } = useUserRole();
  const [authPermissions, setAuthPermissions] = useState<GameAuthorizationPermissions>({
    canEditBasicInfo: false,
    canEditTeams: false,
    canEditSettings: false,
    canEditApprovalStatus: false,
    canViewGame: true,
    role: permissions.role,
    isGameCreator: false,
    isVenueManager: false,
    isTeamManager: false,
    isAdmin: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (gameData && userId) {
      checkAuthorization();
    }
  }, [gameData, userId, permissions.role]);

  const checkAuthorization = async () => {
    try {
      setLoading(true);
      
      const isAdmin = permissions.role === 'admin';
      const isGameCreator = gameData.created_by === userId || gameData.posted_by === userId;
      
      // Enhanced venue manager detection for both Supabase and internal users
      const isVenueManager = (
        (gameData.venue_id === userId || gameData.venue_id === userId?.toString()) && 
        (permissions.role === 'venue' || permissions.isVenue)
      ) || (
        // Check if user is venue by comparing with venue name/ID
        gameData.venue_name === permissions.role && permissions.isVenue
      );
      
      console.log('=== VENUE AUTHORIZATION DEBUG ===');
      console.log('Game venue_id:', gameData.venue_id);
      console.log('User ID:', userId);
      console.log('User role:', permissions.role);
      console.log('Is venue user:', permissions.isVenue);
      console.log('Is venue manager:', isVenueManager);
      
      // Check if user is a team manager (owner or coach) for involved teams
      let isTeamManager = false;
      const teamIds = [gameData.home_team_id, gameData.away_team_id].filter(Boolean);
      
      if (teamIds.length > 0) {
        // Check if user is team owner
        const { data: ownedTeams } = await supabase
          .from('teams')
          .select('team_id')
          .eq('created_by', userId)
          .in('team_id', teamIds);
        
        if (ownedTeams && ownedTeams.length > 0) {
          isTeamManager = true;
        } else {
          // Check if user is team coach
          const { data: coachedTeams } = await supabase
            .from('team_coaches')
            .select('team_id')
            .eq('coach_id', userId)
            .in('team_id', teamIds);
          
          if (coachedTeams && coachedTeams.length > 0) {
            isTeamManager = true;
          }
        }
      }

      // Determine permissions based on roles and relationships
      const newPermissions: GameAuthorizationPermissions = {
        canEditBasicInfo: isAdmin || isGameCreator || isVenueManager,
        canEditTeams: isAdmin || isGameCreator || isVenueManager || (isTeamManager && permissions.isCoach),
        canEditSettings: isAdmin || isGameCreator || isVenueManager,
        canEditApprovalStatus: isAdmin || isVenueManager,
        canViewGame: true, // Everyone can view games for now
        role: permissions.role,
        isGameCreator,
        isVenueManager,
        isTeamManager,
        isAdmin,
      };

      setAuthPermissions(newPermissions);
      
    } catch (error) {
      console.error('Error checking game authorization:', error);
      // Default to minimal permissions on error
      setAuthPermissions(prev => ({
        ...prev,
        canEditBasicInfo: false,
        canEditTeams: false,
        canEditSettings: false,
        canEditApprovalStatus: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  return {
    permissions: authPermissions,
    loading,
    refetch: checkAuthorization,
  };
};